import { useSyncExternalStore } from "react";

/**
 * Escape hatch for the native mobile home. HomeGate renders the mobile home
 * over `/` on native; setting this session flag lets the user open the full
 * desktop-style home instead (and the drawer's "Mobile home" item clears it).
 *
 * The flag is subscribable so the gates (`HomeGate`, `WorkspaceGate`) can
 * re-render in place when "Open full app" is tapped on the very route they
 * guard — a plain sessionStorage read only re-evaluates on navigation.
 */
const FULL_APP_KEY = "cohesive-mobile-full-app";

const listeners = new Set<() => void>();

export function isFullAppOverride(): boolean {
  try {
    return sessionStorage.getItem(FULL_APP_KEY) === "1";
  } catch {
    return false;
  }
}

export function setFullAppOverride(on: boolean): void {
  try {
    if (on) {
      sessionStorage.setItem(FULL_APP_KEY, "1");
    } else {
      sessionStorage.removeItem(FULL_APP_KEY);
    }
  } catch {
    /* sessionStorage unavailable — ignore */
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive read of the override — re-renders the caller when it flips. */
export function useFullAppOverride(): boolean {
  return useSyncExternalStore(subscribe, isFullAppOverride, () => false);
}
